import { Heart } from "lucide-react";
import { useEffect, useState, type MouseEvent } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { useEchoSaves } from "./useEchoSaves";

/**
 * "Like & Save" heart for one echo. Compact icon in list rows, labelled button
 * on the echo page. The pressed state flips immediately and settles on the
 * server's answer once the saves list refetches; a failure notifies and reverts.
 */
export const EchoSaveButton = ({
  echoId,
  labelled = false,
  className,
}: {
  echoId: string;
  labelled?: boolean;
  className?: string;
}) => {
  const { isSaved, toggle, isPending } = useEchoSaves();
  const saved = isSaved(echoId);
  const [optimistic, setOptimistic] = useState<boolean | null>(null);
  useEffect(() => setOptimistic(null), [saved]);
  const active = optimistic ?? saved;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    // Rows navigate on click; the heart must not.
    event.preventDefault();
    event.stopPropagation();
    setOptimistic(!active);
    toggle(echoId);
  };

  const title = active
    ? "Remove from your saved echoes"
    : "Like & save this echo";
  const heart = (
    <Heart
      className={cn(
        "size-4 transition-colors",
        active ? "fill-rose-500 text-rose-500" : "text-muted-foreground",
      )}
      aria-hidden="true"
    />
  );

  if (labelled) {
    return (
      <Button
        type="button"
        variant={active ? "secondary" : "outline"}
        size="sm"
        className={className}
        onClick={handleClick}
        disabled={isPending}
        aria-pressed={active}
        title={title}
        data-testid="echo-save-button"
      >
        {heart}
        {active ? "Saved" : "Like & Save"}
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn("size-8 rounded-full", className)}
      onClick={handleClick}
      disabled={isPending}
      aria-pressed={active}
      aria-label={title}
      title={title}
      data-testid="echo-save-button"
    >
      {heart}
    </Button>
  );
};
